/**
 * Predefined (static) section and fields for the Exit Feedback form.
 * Shown in ExitFeedbackFormBuilder as a toggle-only (non-deletable) set and
 * merged into the dynamic form rendered to a resigning employee in FeedbackForm.
 *
 * Shape mirrors constants/onboardingPredefinedFields.js exactly:
 *   PREDEFINED_SECTION: { _id, sectionName, sectionKey, isDefault, isPredefined, order }
 *   PREDEFINED_FIELDS[]: { fieldName, fieldLabel, fieldType, required, order, options?, placeholder? }
 */

export const PREDEFINED_SECTION_KEY = "exit_feedback_predefined";
export const PREDEFINED_SECTION_NAME = "Exit Feedback Questions";

export const PREDEFINED_SECTION = {
  _id: "predefined_section",
  sectionName: PREDEFINED_SECTION_NAME,
  sectionKey: PREDEFINED_SECTION_KEY,
  isDefault: true,
  isPredefined: true,
  order: -1,
};

/** Options for the "reason(s) for leaving" multiselect - same 8 values as today's checkbox group. */
const REASON_FOR_LEAVING_OPTIONS = [
  { key: "Dissatisfaction with job", value: "dissatisfaction_with_job" },
  { key: "Relocation", value: "relocation" },
  { key: "Transportation Difficulties", value: "transportation_difficulties" },
  { key: "Better Opportunity", value: "better_opportunity" },
  { key: "Health Condition / Disability", value: "health_condition_disability" },
  { key: "Home Duties / Family Obligations", value: "home_duties_family_obligations" },
  { key: "More Pay", value: "more_pay" },
  { key: "Career Changes", value: "career_changes" },
];

/** Shared 5-point agree/disagree scale reused by every "select" question below. */
const AGREEMENT_SCALE_OPTIONS = [
  { key: "Strongly Agree", value: "strongly_agree" },
  { key: "Agree", value: "agree" },
  { key: "Neither Agree Nor Disagree", value: "neither_agree_nor_disagree" },
  { key: "Disagree", value: "disagree" },
  { key: "Strongly Disagree", value: "strongly_disagree" },
];

/** Predefined fields: fieldName, fieldLabel, fieldType, required, options (for select/multiselect/checkbox), placeholder, order */
export const PREDEFINED_FIELDS = [
  {
    fieldName: "reason_for_leaving",
    fieldLabel: "What was your reason(s) for leaving company? (Check all items that apply)",
    fieldType: "multiselect",
    required: true,
    options: REASON_FOR_LEAVING_OPTIONS,
    order: 0,
  },
  {
    fieldName: "work_activity",
    fieldLabel: "I enjoyed my work activities.",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 1,
  },
  {
    fieldName: "challenge_job",
    fieldLabel: "I was challenged by my job.",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 2,
  },
  {
    fieldName: "paid_fairly",
    fieldLabel: "I was paid fairly for my work.",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 3,
  },
  {
    fieldName: "equitable_treatment",
    fieldLabel: "My supervisor demonstrated fair & equitable treatment",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 4,
  },
  {
    fieldName: "responsibilities",
    fieldLabel: "My supervisor gave clear instructions for work assignments and responsibilities.",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 5,
  },
  {
    fieldName: "job_responsibilities",
    fieldLabel: "My job description accurately reflects my job responsibilities",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 6,
  },
  {
    fieldName: "performance_appraisal",
    fieldLabel: "I was given honest, worthwhile performance appraisals",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 7,
  },
  {
    fieldName: "operated_efficiently",
    fieldLabel: "My department operated efficiently",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 8,
  },
  {
    fieldName: "adequately_staffed",
    fieldLabel: "My department was adequately staffed.",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 9,
  },
  {
    fieldName: "upper_management",
    fieldLabel: "Upper management communicates well with employees",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 10,
  },
  {
    fieldName: "career_advancement",
    fieldLabel: "I felt there were opportunities for career advancement",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 11,
  },
  {
    fieldName: "recommend_employer",
    fieldLabel: "I would recommend this company as an employer to friends/family",
    fieldType: "select",
    required: true,
    options: AGREEMENT_SCALE_OPTIONS,
    order: 12,
  },
  {
    fieldName: "employee_comment",
    fieldLabel: "Employee Comments",
    fieldType: "textarea",
    required: false,
    order: 13,
  },
  {
    fieldName: "credentials",
    fieldLabel: "Credentials",
    fieldType: "textarea",
    required: true,
    placeholder: "Enter any company-related account credentials you hold (or N/A)",
    order: 14,
  },
  {
    fieldName: "terms_and_condition",
    fieldLabel: "I accept terms and conditions",
    fieldType: "checkbox",
    required: true,
    options: [{ key: "Yes, I accept", value: "accepted" }],
    order: 15,
  },
];
